import classNames from "classnames";
import EventTableRow from "./EventTableRow";
import Pagination from "./Pagination";
import EmptyState from "./EmptyState";
import { TableSkeleton } from "./LoadingSkeleton";

const COLUMNS = [
  { field: "name", label: "Event" },
  { field: "start_date", label: "Starts" },
  { field: "end_date", label: "Ends" },
  { field: "impressions", label: "Impressions" },
  { field: "views", label: "Views" },
  { field: "ratio", label: "Ratio" },
  { field: null, label: "Status" },
  { field: null, label: "Trend" },
];

// Custom SVG (not Material Icon ligatures — arrow_upward/arrow_downward/unfold_more
// render at noticeably different visual weights even at the same font-size, which
// made the header jump in scale on sort). Arrow shape chosen over chevrons for
// visibility; size stays fixed across sorted/unsorted states.
// Two distinct icons, matching the Figma table header exactly — not one icon
// with dimmed states. Unsorted columns show a double chevron (up+down, both
// visible). Sorting swaps it entirely for a single bold arrow in that direction.
const ChevronUpDownIcon = () => (
  <svg className="ea-table__sort-icon" width="12" height="14" viewBox="0 0 12 14" fill="none" aria-hidden="true">
    <path d="M2.5 5.5L6 2L9.5 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2.5 8.5L6 12L9.5 8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowIcon = ({ direction }) => (
  <svg className="ea-table__sort-icon ea-table__sort-icon--active" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    {direction === "asc" ? (
      <path d="M7 11V3M7 3L3 7M7 3L11 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    ) : (
      <path d="M7 3V11M7 11L3 7M7 11L11 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    )}
  </svg>
);

const SortIcon = ({ active, direction }) => (active ? <ArrowIcon direction={direction} /> : <ChevronUpDownIcon />);

const EventTable = ({
  events,
  pagination,
  sort,
  onSortChange,
  page,
  onPageChange,
  selectedEventUuid,
  onSelectEvent,
  loading,
  error,
  onWidenRange,
  widenLabel,
}) => {
  const handleHeaderClick = (field) => {
    if (!field) return;
    const nextDirection = sort.field === field && sort.direction === "desc" ? "asc" : "desc";
    onSortChange({ field, direction: nextDirection });
  };

  if (loading) return <TableSkeleton />;
  if (error) {
    return <EmptyState title="Couldn't load events" description={error} />;
  }
  if (!events.length) {
    return (
      <EmptyState
        actionLabel={onWidenRange && widenLabel ? widenLabel : undefined}
        onAction={onWidenRange && widenLabel ? onWidenRange : undefined}
      />
    );
  }

  return (
    <div className="ea-table-wrap">
      <table className="ea-table">
        <thead>
          <tr>
            {COLUMNS.map(({ field, label }) => (
              <th
                key={label}
                className={classNames("ea-table__header-cell", { "ea-table__header-cell--sortable": !!field })}
                onClick={() => handleHeaderClick(field)}
              >
                {label}
                {field && <SortIcon active={sort.field === field} direction={sort.direction} />}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <EventTableRow
              key={event.event_uuid}
              event={event}
              isSelected={event.event_uuid === selectedEventUuid}
              onSelect={onSelectEvent}
            />
          ))}
        </tbody>
      </table>
      <Pagination page={page} totalPages={pagination.total_pages} onPageChange={onPageChange} />
    </div>
  );
};

export default EventTable;
